import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, MailCheck, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
// import { BASE_URL } from '../../../config/clientConfig';

const LoginForm = ({ onCreateAccount }) => {
  const { login, state, clearError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm]           = useState({ email: '', password: '' });
  const [showPw, setShowPw]       = useState(false);
  const [view, setView]           = useState('login');
  const [fpEmail, setFpEmail]     = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError]     = useState('');
  // const googleAuthUrl = `${BASE_URL}/api/v1/auth/google`;

  const handleChange = (e) => {
    clearError();
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await login(form.email, form.password);
      // Route based on role from the API response
      const role = res?.data?.user?.role;
      navigate(role === "admin" ? "/admin" : "/dashboard");
    } catch {}
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      await authService.forgotPassword(fpEmail);
      setView('sent');
    } catch (err) {
      setFpError(err.message);
    } finally {
      setFpLoading(false);
    }
  };

  const goBackToLogin = () => {
    setView('login');
    setFpEmail('');
    setFpError('');
    clearError();
  };

  if (view === 'sent') return (
    <div className="w-full max-w-md flex flex-col items-center text-center gap-5">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
        <MailCheck className="w-8 h-8 text-primary" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-brand-text">Check Your Inbox</h2>
        <p className="text-sm text-brand-muted leading-relaxed">
          We sent a password reset link to{' '}
          <span className="font-semibold text-brand-text">{fpEmail}</span>.
          Please check your email and follow the instructions.
        </p>
      </div>
      <button
        onClick={goBackToLogin}
        className="w-full py-3 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors"
      >
        Okay, Back to Login
      </button>
    </div>
  );

  if (view === 'forgot') return (
    <>
      {fpLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
            <p className="text-sm font-semibold text-brand-text">Sending reset link…</p>
            <p className="text-xs text-brand-muted">Please wait, this will just take a moment.</p>
          </div>
        </div>
      )}
      <div className="w-full max-w-md">
      <button
        onClick={goBackToLogin}
        className="flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-text transition mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to login
      </button>
      <h1 className="text-2xl font-bold text-brand-text mb-1">Forgot password?</h1>
      <p className="text-brand-muted text-sm mb-8">
        Enter your email and we will send you a reset link.
      </p>
      <form onSubmit={handleForgotSubmit} className="space-y-5">
        {fpError && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
            {fpError}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Email address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light pointer-events-none" />
            <input
              type="email"
              value={fpEmail}
              onChange={e => setFpEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="w-full pl-10 pr-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={fpLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {fpLoading ? 'Sending…' : <><span>Send Reset Link</span><ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
    </>
  );

  return (
    <>
      {state.isLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
            <p className="text-sm font-semibold text-brand-text">Signing in…</p>
            <p className="text-xs text-brand-muted">Please wait, this will just take a moment.</p>
          </div>
        </div>
      )}
      <div className="w-full max-w-md">
      <h1 className="text-2xl font-bold text-brand-text mb-1">Welcome back</h1>
      <p className="text-brand-muted text-sm mb-8">Sign in to your AIPolicyScan account</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        {state.error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
            {state.error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Email address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light pointer-events-none" />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@email.com"
              required
              className="w-full pl-10 pr-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-text mb-1.5">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light pointer-events-none" />
            <input
              type={showPw ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="••••••••"
              required
              className="w-full pl-10 pr-10 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-light hover:text-brand-muted transition"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="flex justify-end -mt-1">
          <button
            type="button"
            onClick={() => { clearError(); setView('forgot'); }}
            className="text-sm text-primary font-medium hover:underline"
          >
            Forgot password?
          </button>
        </div>
        <button
          type="submit"
          disabled={state.isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {state.isLoading ? 'Signing in…' : <><span>Sign In</span><ArrowRight className="w-4 h-4" /></>}
        </button>

        {/* <button
          type="button"
          onClick={() => {
            window.location.href = googleAuthUrl;
          }}
          className="w-full flex items-center justify-center gap-2 border border-surface-border bg-white text-brand-text py-3 rounded-lg font-semibold hover:bg-surface transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path
              fill="#EA4335"
              d="M12 10.2v3.9h5.5c-.2 1.2-1.4 3.6-5.5 3.6-3.3 0-6-2.8-6-6.2s2.7-6.2 6-6.2c1.9 0 3.1.8 3.9 1.5l2.7-2.7C16.9 2.5 14.7 1.6 12 1.6 6.9 1.6 2.8 5.9 2.8 11s4.1 9.4 9.2 9.4c5.3 0 8.8-3.7 8.8-8.9 0-.6-.1-1-.1-1.3H12z"
            />
            <path
              fill="#34A853"
              d="M3.9 7.3l3.2 2.3c.9-2 2.8-3.4 4.9-3.4 1.9 0 3.1.8 3.9 1.5l2.7-2.7C16.9 2.5 14.7 1.6 12 1.6 8.5 1.6 5.4 3.7 3.9 7.3z"
            />
            <path
              fill="#FBBC05"
              d="M12 20.4c2.6 0 4.8-.9 6.4-2.5l-3-2.4c-.8.6-1.9 1.1-3.4 1.1-3.9 0-5.3-2.8-5.6-4.1l-3.2 2.5c1.5 3.6 4.6 5.4 8.8 5.4z"
            />
            <path
              fill="#4285F4"
              d="M20.8 11.5c0-.6-.1-1-.1-1.3H12v3.9h5.5c-.3 1.3-1.2 2.3-2.1 3l3 2.4c1.8-1.7 2.4-4.2 2.4-8z"
            />
          </svg>
          Continue with Google
        </button> */}

      </form>
      <p className="text-center text-sm text-brand-muted mt-6">
        Don't have an account?{' '}
        <button onClick={onCreateAccount} className="text-primary font-semibold hover:underline">
          Create account
        </button>
      </p>
    </div>
    </>
  );
};

export default LoginForm;
